import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function LoginPage() {
  const { login, formatApiError } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@ledgerloom.com");
  const [password, setPassword] = useState("admin123");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/dashboard");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <div
        className="hidden lg:block relative"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1483366774565-c783b9f70e2c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwYXJjaGl0ZWN0dXJlJTIwYnVpbGRpbmclMjBmYWNhZGV8ZW58MHx8fHwxNzg0NzE0NjU0fDA&ixlib=rb-4.1.0&q=85)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-primary/60" />
        <div className="absolute bottom-10 left-10 right-10 text-primary-foreground">
          <div className="overline text-primary-foreground/80 mb-4">
            LedgerLoom · Est. 2026
          </div>
          <h2 className="font-serif-display text-5xl leading-tight tracking-tight">
            The quiet ledger for
            <br /> serious operators.
          </h2>
          <p className="mt-4 text-sm max-w-md text-primary-foreground/80">
            Double-entry bookkeeping, multi-company consolidation, and bank
            reconciliation — engineered for accountants who prefer precision
            over noise.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6">
          <div>
            <div className="overline mb-3">Sign In</div>
            <h1 className="font-serif-display text-4xl leading-none tracking-tight">
              Welcome back.
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Access your ledgers, journals, and reports.
            </p>
          </div>

          <div className="space-y-3">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-widest">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1 rounded-sm h-11 font-mono"
              />
            </div>
            <div>
              <Label htmlFor="pw" className="text-xs uppercase tracking-widest">
                Password
              </Label>
              <Input
                id="pw"
                type="password"
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1 rounded-sm h-11 font-mono"
              />
            </div>
          </div>

          {err && (
            <div
              data-testid="login-error"
              className="text-sm text-destructive border border-destructive/40 bg-destructive/5 px-3 py-2"
            >
              {err}
            </div>
          )}

          <Button
            type="submit"
            disabled={busy}
            data-testid="login-submit"
            className="w-full h-11 rounded-sm font-mono uppercase tracking-widest text-xs"
          >
            {busy ? "Signing in…" : "Enter Ledger"}
          </Button>

          <div className="relative py-2">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-background px-3 text-[10px] uppercase tracking-widest text-muted-foreground">
                or
              </span>
            </div>
          </div>

          {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          <Button
            type="button"
            variant="outline"
            data-testid="google-signin"
            onClick={() => {
              const redirectUrl = window.location.origin + "/";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full h-11 rounded-sm font-mono uppercase tracking-widest text-xs gap-2"
          >
            <svg width="16" height="16" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            Continue with Google
          </Button>

          <div className="text-xs text-muted-foreground text-center">
            No account?{" "}
            <Link
              to="/register"
              data-testid="go-register"
              className="underline text-foreground"
            >
              Create one
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
